import React, { useState, useEffect } from 'react';
import { getUserProfile, updatePersonalDetails, uploadProfilePicture, deleteProfilePicture } from '../../../api/user/account';
import './AccountManagement.css';
// import bundled default image (file must exist in same folder)
import defaultProfile from './default-profile.jpg';
import Loader from '../../common/Loader';

const PersonalDetails = () => {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    tell: '',
    dob: '',
    gender: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(defaultProfile); // default to imported image

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      const response = await getUserProfile();
      if (response.success) {
        setUser(response.user);
        setFormData({
          first_name: response.user.first_name || '',
          last_name: response.user.last_name || '',
          email: response.user.email || '',
          tell: response.user.tell || '',
          dob: response.user.dob || '',
          gender: response.user.gender || ''
        });

        // Set profile image with proper URL 
        if (response.user.profile && response.user.profile !== 'default-profile.jpg') {
          setProfileImage(response.user.profile);
          setImagePreview(`${response.user.profile}`);
        } else {
          setProfileImage('default-profile.jpg');
          setImagePreview(defaultProfile);
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await updatePersonalDetails(formData);
      if (response.success) {
        setMessage({ type: 'success', text: response.message });
        setUser(response.user);
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('profile', file);

    try {
      setIsLoading(true);
      const response = await uploadProfilePicture(formData);
      if (response.success) {
        setProfileImage(response.user.profile);
        setImagePreview(`${response.user.profile}`);
        setMessage({ type: 'success', text: response.message });

        // Refresh user data
        fetchUserProfile();
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      // Revert preview on error
      if (profileImage && profileImage !== 'default-profile.jpg') {
        setImagePreview(`${profileImage}`);
      } else {
        setImagePreview(defaultProfile);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveImage = async () => {
    try {
      setIsLoading(true);
      const response = await deleteProfilePicture();
      if (response.success) {
        setProfileImage('default-profile.jpg');
        setImagePreview(defaultProfile);
        setMessage({ type: 'success', text: response.message });

        // Refresh user data
        fetchUserProfile();
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !user) {
    return <Loader />;
  }

  const fullName = `${formData.first_name || 'Your'} ${formData.last_name || 'Name'}`.trim();
  const hasCustomPhoto = profileImage && profileImage !== 'default-profile.jpg';

  return (
    <div className="account-management-section">
      {/* Compact header */}
      <div className="pd-header">
        <div className="pd-header-icon">
          <i className="fa fa-id-card" aria-hidden="true"></i>
        </div>
        <div className="pd-header-text">
          <h2 className="pd-header-title">Personal Details</h2>
          <p className="pd-header-subtitle">Update your personal information</p>
        </div>
        <span className="pd-header-badge">
          <i className="fa fa-shield" aria-hidden="true"></i>
          Secure
        </span>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          <i className={`fa ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} aria-hidden="true"></i>
          {message.text}
        </div>
      )}

      <div className="pd-body">
        {/* Profile card */}
        <div className="pd-profile-card">
          <div className="pd-avatar-wrap">
            <img
              src={imagePreview || defaultProfile}
              alt="Profile"
              className="pd-avatar"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = defaultProfile;
              }}
            />
            <label htmlFor="pd-profile-upload" className="pd-avatar-camera" title="Change photo">
              <i className="fa fa-camera" aria-hidden="true"></i>
            </label>
            <input
              id="pd-profile-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
          </div>

          <div className="pd-profile-info">
            <h3 className="pd-profile-name">{fullName}</h3>
            <p className="pd-profile-email">
              <i className="fa fa-envelope" aria-hidden="true"></i>
              {formData.email || 'No email provided'}
            </p>
            {user?.isApprovedProvider ? (
              <span className="pd-member-badge pd-badge-provider">
                <i className="fa fa-briefcase" aria-hidden="true"></i>
                Service Provider
              </span>
            ) : (
              <span className="pd-member-badge">
                <i className="fa fa-user-check" aria-hidden="true"></i>
                Spotfli member
              </span>
            )}
          </div>

          <div className="pd-profile-actions">
            <label htmlFor="pd-profile-upload" className="pd-btn pd-btn-primary">
              <i className="fa fa-camera" aria-hidden="true"></i>
              Change Photo
            </label>
            {hasCustomPhoto && (
              <button
                type="button"
                className="pd-btn pd-btn-danger"
                onClick={handleRemoveImage}
                disabled={isLoading}
              >
                <i className="fa fa-trash" aria-hidden="true"></i>
                Remove
              </button>
            )}
          </div>
        </div>

        {/* Details form */}
        <form className="pd-form" onSubmit={handleSubmit}>
          <div className="pd-form-section-title">
            <i className="fa fa-user" aria-hidden="true"></i>
            Personal Information
          </div>

          <div className="pd-form-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="first_name">First Name *</label>
              <div className="pd-input-wrap">
                <i className="fa fa-user" aria-hidden="true"></i>
                <input
                  className="form-input"
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="last_name">Last Name *</label>
              <div className="pd-input-wrap">
                <i className="fa fa-user-circle" aria-hidden="true"></i>
                <input
                  className="form-input"
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group pd-span-2">
              <label className="form-label" htmlFor="email">Email Address *</label>
              <div className="pd-input-wrap">
                <i className="fa fa-envelope" aria-hidden="true"></i>
                <input
                  className="form-input"
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled
                />
                <span className="pd-input-lock" title="Email cannot be changed">
                  <i className="fa fa-lock" aria-hidden="true"></i>
                </span>
              </div>
              <div className="input-note">
                <i className="fa fa-info-circle" aria-hidden="true"></i>
                Email cannot be changed. Contact support if needed.
              </div>
            </div>

            <div className="form-group pd-span-2">
              <label className="form-label" htmlFor="tell">Phone Number</label>
              <div className="pd-input-wrap">
                <i className="fa fa-phone" aria-hidden="true"></i>
                <input
                  className="form-input"
                  type="tel"
                  id="tell"
                  name="tell"
                  value={formData.tell}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="dob">Date of Birth</label>
              <div className="pd-input-wrap">
                <i className="fa fa-calendar" aria-hidden="true"></i>
                <input
                  className="form-input"
                  type="date"
                  id="dob"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Gender</label>
              <div className="gender-options pd-gender">
                <label className="gender-option">
                  <input type="radio" name="gender" value="male" checked={formData.gender === 'male'} onChange={handleChange} />
                  <span className="radio-custom" aria-hidden="true">
                    <i className="fa fa-mars"></i>
                  </span>
                  <span className="gender-option-label">Male</span>
                  <i className="fa fa-check gender-check" aria-hidden="true"></i>
                </label>
                <label className="gender-option">
                  <input type="radio" name="gender" value="female" checked={formData.gender === 'female'} onChange={handleChange} />
                  <span className="radio-custom" aria-hidden="true">
                    <i className="fa fa-venus"></i>
                  </span>
                  <span className="gender-option-label">Female</span>
                  <i className="fa fa-check gender-check" aria-hidden="true"></i>
                </label>
                <label className="gender-option">
                  <input type="radio" name="gender" value="other" checked={formData.gender === 'other'} onChange={handleChange} />
                  <span className="radio-custom" aria-hidden="true">
                    <i className="fa fa-genderless"></i>
                  </span>
                  <span className="gender-option-label">Other</span>
                  <i className="fa fa-check gender-check" aria-hidden="true"></i>
                </label>
              </div>
            </div>
          </div>

          <div className="form-actions pd-form-actions">
            <button
              type="submit"
              className="save-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="btn-spinner"></div>
                  Updating...
                </>
              ) : (
                <>
                  <i className="fa fa-save" aria-hidden="true"></i>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonalDetails;
